'use client';

import React, { useState, useEffect } from 'react';

// ─── Design Tokens (extracted from npplfconsulting.com) ───────────────────────
// Colors:
//   bg:        #0d2218  (main background)
//   bgDark:    #0a1c12  (header/footer/section backgrounds)
//   gold:      #C9A84C  (primary accent)
//   goldLight: #e8d5a3  (secondary text / headings)
//   goldMuted: #B8922A  (muted gold)
//   sage:      #a8c4b0  (body text)
// Fonts:
//   display:   'Cormorant Garamond', serif  (headings)
//   sans:      'Inter', sans-serif          (body)

// ─── Types ───────────────────────────────────────────────────────────────────
interface PillarItem {
  emoji: string;
  title: string;
  description: string;
}

interface Pillar {
  id: string;
  emoji: string;
  heading: string;
  subheading: string;
  items: PillarItem[];
  footerEmoji: string;
  footerText: string;
}

interface Module {
  number: string;
  title: string;
  subtitle: string;
  emoji: string;
  topics: string[];
  assignments: string[];
  tech?: string[];
}

interface Objective {
  text: string;
}

// ─── Data ────────────────────────────────────────────────────────────────────
const pillars: Pillar[] = [
  {
    id: 'niche',
    emoji: '🎯',
    heading: 'MODULE I\nNICHE',
    subheading: 'IDENTITY, ALIGNMENT\n& STRATEGIC PRACTICE DEVELOPMENT',
    items: [
      {
        emoji: '🎯',
        title: 'NICHE ALIGNMENT',
        description:
          'Identify practice areas aligned with personal strengths, values, interests, and long-term professional goals.',
      },
      {
        emoji: '📍',
        title: 'MARKET POSITIONING',
        description:
          'Develop authority within a niche, differentiate from competitors, and build boutique firm identity.',
      },
      {
        emoji: '⚖️',
        title: 'ETHICAL MARKETING',
        description:
          'Explore ethical considerations in niche marketing and authentic legal career development.',
      },
      {
        emoji: '✨',
        title: 'PRACTICE VISION',
        description:
          'Build a practice attorneys genuinely enjoy leading through lifestyle-centered law firm planning.',
      },
    ],
    footerEmoji: '🎯',
    footerText: 'Niche creates alignment.\nEverything else gets easier.',
  },
  {
    id: 'practice',
    emoji: '⚙️',
    heading: 'MODULE II\nPRACTICE &\nPROCEDURES',
    subheading: 'LAW FIRM OPERATIONS,\nSYSTEMS & LEGAL TECHNOLOGY',
    items: [
      {
        emoji: '⚙️',
        title: 'LEGAL OPERATIONS',
        description:
          'Case management systems, intake procedures, workflow design, and litigation infrastructure fundamentals.',
      },
      {
        emoji: '🤖',
        title: 'AI-ASSISTED TOOLS',
        description:
          'Evaluate Clio, MyCase, ChatGPT, Lexis+ AI, Harvey, CoCounsel, and Microsoft Copilot for legal practice.',
      },
      {
        emoji: '👥',
        title: 'STAFFING & DELEGATION',
        description:
          'Build the right team structure, delegation models, and organizational systems for operational health.',
      },
      {
        emoji: '📊',
        title: 'WORKFLOW EFFICIENCY',
        description:
          'Document workflows, automate repeatable processes, and spend more time on high-value legal work.',
      },
    ],
    footerEmoji: '⚙️',
    footerText: 'Structure creates clarity.\nSystems create sustainability.',
  },
  {
    id: 'profit',
    emoji: '📈',
    heading: 'MODULE III\nPROFIT &\nGROWTH',
    subheading: 'STRATEGIC PROFITABILITY\n& SUSTAINABLE DEVELOPMENT',
    items: [
      {
        emoji: '📈',
        title: 'PROFITABILITY STRATEGY',
        description:
          'Understand profitability drivers, client alignment, pricing structure, and workflow efficiency.',
      },
      {
        emoji: '🏗️',
        title: 'SCALABLE GROWTH',
        description:
          'Operational scalability, sustainable growth models, and small firm versus high-volume analysis.',
      },
      {
        emoji: '❤️',
        title: 'ATTORNEY WELLNESS',
        description:
          'Address burnout, operational overload, and build firms that support long-term sustainability.',
      },
      {
        emoji: '💼',
        title: 'BUSINESS DEVELOPMENT',
        description:
          'Authentic business development strategies that feel natural when you love the field you practice.',
      },
    ],
    footerEmoji: '📈',
    footerText: 'Systems create profitability.\nStrategy creates long-term control.',
  },
  {
    id: 'exit',
    emoji: '🚪',
    heading: 'MODULE IV\nEXIT PLAN\n& CONTROL',
    subheading: 'SUCCESSION PLANNING,\nENTERPRISE VALUE & LONG-TERM FREEDOM',
    items: [
      {
        emoji: '🚪',
        title: 'SUCCESSION PLANNING',
        description:
          'Design your transition on your terms — sell, merge, wind down, or step away with intention.',
      },
      {
        emoji: '👑',
        title: 'ENTERPRISE VALUE',
        description:
          'Understand law firm valuation, transferable business assets, and legacy development concepts.',
      },
      {
        emoji: '📅',
        title: 'RETIREMENT-CONSCIOUS DESIGN',
        description:
          'Build retirement-conscious infrastructure and operational continuity from day one.',
      },
      {
        emoji: '🔓',
        title: 'FREEDOM & CONTROL',
        description:
          'Create long-term flexibility, financial stability, and greater control over your professional future.',
      },
    ],
    footerEmoji: '👑',
    footerText: 'Strategic planning creates\nlong-term freedom and control.',
  },
];

const modules: Module[] = [
  {
    number: 'I',
    title: 'NICHE',
    subtitle: 'Identity, Alignment & Strategic Practice Development',
    emoji: '🎯',
    topics: [
      'Identifying authentic practice interests',
      'Strength-based practice design',
      'Lifestyle-centered law firm planning',
      'Market positioning and differentiation',
      'Boutique vs. large firm models',
      'Developing authority within a niche',
      'Ethical considerations in niche marketing',
      'Building a practice attorneys genuinely enjoy leading',
    ],
    assignments: [
      'Niche Evaluation Exercise',
      'Personal Practice Vision Statement',
      'Market Position Analysis',
    ],
  },
  {
    number: 'II',
    title: 'PRACTICE & PROCEDURES',
    subtitle: 'Law Firm Operations, Systems & Legal Technology',
    emoji: '⚙️',
    topics: [
      'Legal operations fundamentals',
      'Case management systems',
      'Intake and onboarding procedures',
      'Workflow design and documentation',
      'Litigation infrastructure',
      'AI-assisted legal workflow systems',
      'Automation and efficiency tools',
      'Client communication systems',
      'Delegation and staffing models',
      'Organizational structure and operational health',
    ],
    assignments: [
      'Workflow Mapping Exercise',
      'AI & Legal Technology Evaluation Memo',
      'Operational Procedures Drafting Project',
    ],
    tech: [
      'Clio',
      'MyCase',
      'PracticePanther',
      'FileVine',
      'Relativity',
      'ChatGPT',
      'Lexis+ AI',
      'Harvey',
      'Microsoft Copilot',
      'CoCounsel',
    ],
  },
  {
    number: 'III',
    title: 'PROFIT & GROWTH',
    subtitle: 'Strategic Profitability & Sustainable Law Firm Development',
    emoji: '📈',
    topics: [
      'Understanding profitability in legal practice',
      'Client alignment and pricing structure',
      'Workflow efficiency and financial health',
      'Operational scalability',
      'Business development through authenticity',
      'Sustainable growth models',
      'Litigation profitability analysis',
      'Small firm versus high-volume models',
      'Attorney burnout and operational overload',
      'Building firms that support long-term sustainability',
    ],
    assignments: [
      'Profitability Analysis Exercise',
      'Firm Structure Evaluation',
      'Operational Efficiency Assessment',
    ],
  },
  {
    number: 'IV',
    title: 'EXIT PLAN & CONTROL',
    subtitle: 'Succession Planning, Enterprise Value & Long-Term Freedom',
    emoji: '🚪',
    topics: [
      'Law firms as transferable business assets',
      'Succession-minded operational planning',
      'Retirement-conscious infrastructure',
      'Transition planning strategies',
      'Law firm valuation concepts',
      'Legacy and enterprise development',
      'Building operational continuity',
      'Creating long-term flexibility and control',
      'Designing practices around long-term personal goals',
    ],
    assignments: [
      'Exit Planning Reflection Paper',
      'Long-Term Law Firm Vision Plan',
      'Final Strategic Practice Blueprint',
    ],
  },
];

const objectives: Objective[] = [
  { text: 'Identify and evaluate legal practice niches aligned with personal strengths, interests, and market opportunities' },
  { text: 'Understand foundational law firm operational systems and procedures' },
  { text: 'Analyze workflow inefficiencies within legal practice environments' },
  { text: 'Evaluate legal technology and AI-assisted legal operations tools' },
  { text: 'Develop scalable intake, case management, and communication systems' },
  { text: 'Understand profitability drivers within litigation and small firm practice' },
  { text: 'Explore attorney wellness, operational sustainability, and burnout prevention' },
  { text: 'Develop strategic growth and transition planning concepts' },
  { text: 'Understand succession-minded and retirement-conscious law firm design' },
  { text: 'Create a foundational operational business plan for a modern legal practice' },
];

// ─── Sub-components ──────────────────────────────────────────────────────────
function GoldDivider({ thick = false }: { thick?: boolean }) {
  return (
    <div
      style={{
        height: thick ? '2px' : '1px',
        background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)',
      }}
    />
  );
}

function CheckItem({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-3">
      <span style={{ color: '#C9A84C', fontSize: '14px', marginTop: '2px', flexShrink: 0 }}>✓</span>
      <span className="text-sm" style={{ color: '#a8c4b0' }}>{text}</span>
    </div>
  );
}

function BulletItem({ text }: { text: string }) {
  return (
    <li className="flex items-start gap-2">
      <span style={{ color: '#C9A84C', fontSize: '10px', marginTop: '3px', flexShrink: 0 }}>✓</span>
      <span className="text-xs" style={{ color: '#a8c4b0' }}>{text}</span>
    </li>
  );
}

function IconCircle({ emoji }: { emoji: string }) {
  return (
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-sm"
      style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)' }}
    >
      {emoji}
    </div>
  );
}

// ─── Section: Navigation ─────────────────────────────────────────────────────
function NavBar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const links = [
    { label: 'Overview', href: '#overview' },
    { label: 'Modules', href: '#modules' },
    { label: 'Instructor', href: '#instructor' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        background: '#0a1c12',
        borderBottom: '1px solid rgba(201,168,76,0.3)',
        boxShadow: scrolled ? '0 4px 24px rgba(0,0,0,0.4)' : 'none',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <div
            className="flex items-center justify-center rounded-full w-12 h-12 flex-shrink-0 overflow-hidden"
            style={{
              border: '2px solid #C9A84C',
              boxShadow: '0 0 10px rgba(201,168,76,0.3)',
            }}
          >
            <img
              src="/assets/images/Screenshot_2026-05-16_203005-1779159125021.png"
              alt="Shirley E. Shepard, Founder of NPPLF-Law School Course"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }}
            />
          </div>
          <div className="hidden sm:block">
            <p
              className="font-black text-xs uppercase leading-none"
              style={{ color: '#C9A84C', letterSpacing: '0.1em', fontFamily: "'Inter', sans-serif" }}
            >
              NPPLF-Law School Course
            </p>
            <p className="text-xs" style={{ color: '#a8c4b0', fontSize: '9px', letterSpacing: '0.05em' }}>
              Niche. Practice. Profit. Exit Plan.
            </p>
          </div>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium transition-colors duration-300 hover:opacity-80"
              style={{ color: '#a8c4b0' }}
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            className="px-5 py-2 rounded font-bold text-sm uppercase tracking-wide transition-all duration-300 hover:opacity-90"
            style={{ background: '#C9A84C', color: '#0a1c12' }}
          >
            Submit Proposal
          </a>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 rounded"
          aria-label="Toggle menu"
          style={{ color: '#C9A84C' }}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            {menuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          className="md:hidden px-6 pb-4 flex flex-col gap-3"
          style={{ background: '#0a1c12', borderTop: '1px solid rgba(201,168,76,0.2)' }}
        >
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium py-2"
              style={{ color: '#a8c4b0' }}
              onClick={() => setMenuOpen(false)}
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            className="text-center py-3 rounded font-bold text-sm uppercase tracking-wide"
            style={{ background: '#C9A84C', color: '#0a1c12' }}
            onClick={() => setMenuOpen(false)}
          >
            Submit Proposal
          </a>
        </div>
      )}
    </header>
  );
}

// ─── Section: Hero Banner ────────────────────────────────────────────────────
function HeroBanner() {
  return (
    <section
      className="w-full py-8 px-6"
      style={{ background: '#0a1c12', borderBottom: '2px solid #C9A84C' }}
      aria-label="Course Proposal Banner"
    >
      <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-center gap-8">
        {/* Left: Founder Photo */}
        <div className="flex-shrink-0 order-first">
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              border: '2px solid #C9A84C',
              boxShadow: '0 0 32px rgba(201,168,76,0.3)',
              width: '260px',
              minHeight: '300px',
            }}
          >
            <img
              src="/assets/images/Screenshot_2026-05-16_203005-1779159125021.png"
              alt="Shirley E. Shepard, Founder of NPPLF-Law School Course"
              style={{ width: '260px', minHeight: '300px', objectFit: 'cover', display: 'block' }}
            />
          </div>
        </div>

        {/* Right: Headline */}
        <div className="flex-1 text-center">
          <div
            className="inline-flex items-center gap-3 px-5 py-2 rounded-full mb-4 whitespace-nowrap"
            style={{ border: '1px solid #C9A84C', background: 'rgba(201,168,76,0.1)' }}
          >
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#C9A84C' }} />
            <span className="text-sm font-bold uppercase tracking-wider" style={{ color: '#C9A84C' }}>
              Proposed California Law School Elective Course
            </span>
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#C9A84C' }} />
          </div>

          <h1
            className="font-black text-3xl md:text-4xl uppercase leading-tight mb-3"
            style={{ color: '#C9A84C', fontFamily: "'Cormorant Garamond', serif" }}
          >
            NPPLF - Law School Course
          </h1>
          <p
            className="text-xl md:text-2xl font-black uppercase mb-2"
            style={{ color: '#e8d5a3', fontFamily: "'Cormorant Garamond', serif" }}
          >
            Niche. Practice. Profit. Exit Plan.
          </p>
          <p className="text-sm font-semibold uppercase tracking-widest mb-6" style={{ color: '#a8c4b0' }}>
            Building Sustainable Law Firms Through Legal Operations,
            <br />
            Strategic Practice Development &amp; Attorney-Centered Business Design
          </p>

          <div
            className="inline-block rounded-xl px-6 py-4 text-left"
            style={{ border: '1px solid rgba(201,168,76,0.3)', background: 'rgba(201,168,76,0.07)' }}
          >
            <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#C9A84C' }}>
              Proposed by:
            </p>
            <p className="text-sm font-bold" style={{ color: '#e8d5a3' }}>
              Shirley E. Shepard, BS, MA, JD, LL.M.
            </p>
            <p className="text-xs" style={{ color: '#a8c4b0' }}>
              Founder, NPPLF-Law School Course
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              {['Upper Division Elective', '2–3 Units', '2L · 3L · LL.M.'].map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-1 rounded text-xs font-bold uppercase"
                  style={{
                    background: 'rgba(201,168,76,0.15)',
                    color: '#C9A84C',
                    border: '1px solid rgba(201,168,76,0.3)',
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section: Course Overview ────────────────────────────────────────────────
function CourseOverview() {
  return (
    <section id="overview" style={{ background: '#0d2218' }}>
      <GoldDivider thick />
      <div
        className="w-full py-10 px-6 text-center"
        style={{ background: '#0a1c12', borderBottom: '1px solid rgba(201,168,76,0.2)' }}
      >
        <p className="text-xs uppercase tracking-widest mb-2" style={{ color: '#C9A84C' }}>
          NPPLF-Law School Course Presents
        </p>
        <h2
          className="font-black text-2xl md:text-3xl uppercase leading-tight"
          style={{ color: '#e8d5a3', fontFamily: "'Cormorant Garamond', serif" }}
        >
          NICHE. PRACTICE. PROFIT. EXIT PLAN.
        </h2>
        <p className="text-sm mt-2 font-semibold uppercase tracking-wide" style={{ color: '#C9A84C' }}>
          Proposed California Law School Elective Course
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Left Column */}
          <div className="flex flex-col gap-8">
            <div>
              <h3 className="font-bold text-lg uppercase tracking-wide mb-4" style={{ color: '#C9A84C' }}>
                Course Overview
              </h3>
              <p className="text-sm leading-relaxed mb-4" style={{ color: '#a8c4b0' }}>
                The legal profession is evolving rapidly through advancements in technology, automation, artificial
                intelligence, changing client expectations, and increasing operational demands. While traditional legal
                education provides essential doctrinal and analytical training, many law students graduate without
                practical exposure to the operational, strategic, and business realities involved in building and
                sustaining a modern law practice.
              </p>
              <p className="text-sm leading-relaxed mb-4" style={{ color: '#a8c4b0' }}>
                <span className="font-bold" style={{ color: '#e8d5a3' }}>
                  &ldquo;Niche. Practice. Profit. Exit Plan.&rdquo;
                </span>{' '}
                is an innovative elective course designed to bridge that gap by introducing students to legal
                operations, workflow systems, AI-assisted legal technology, law firm infrastructure, profitability
                strategy, operational sustainability, and long-term professional planning.
              </p>
              <p className="text-sm leading-relaxed" style={{ color: '#a8c4b0' }}>
                Developed through NPPLF-Law School Course and grounded in more than 20 years of litigation and legal
                operations experience across plaintiff and defense law firms, the course examines how attorneys can
                intentionally design practices aligned with their strengths, interests, lifestyle goals, and long-term
                vision while simultaneously building systems that support efficiency, profitability, scalability, and
                sustainability.
              </p>
            </div>

            <GoldDivider />

            <div>
              <h3 className="font-bold text-lg uppercase tracking-wide mb-4" style={{ color: '#C9A84C' }}>
                Core Course Philosophy
              </h3>
              <p className="text-sm leading-relaxed mb-4" style={{ color: '#a8c4b0' }}>
                The course is grounded in the belief that the most successful attorneys build practices aligned with{' '}
                <em style={{ color: '#e8d5a3' }}>who they are,</em>
              </p>
              <div
                className="rounded-xl p-5"
                style={{ border: '1px solid rgba(201,168,76,0.3)', background: 'rgba(201,168,76,0.07)' }}
              >
                <p className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: '#C9A84C' }}>
                  Students will explore how:
                </p>
                <ul className="space-y-2">
                  {[
                    { bold: 'Niche', rest: 'creates alignment' },
                    { bold: 'Structure', rest: 'creates operational clarity' },
                    { bold: 'Systems', rest: 'create sustainability' },
                    { bold: 'Strategic planning', rest: 'creates profitability and long-term control' },
                  ].map((item) => (
                    <li key={item.bold} className="flex items-start gap-2">
                      <span style={{ color: '#C9A84C', fontSize: '10px', marginTop: '3px', flexShrink: 0 }}>✓</span>
                      <span className="text-sm" style={{ color: '#a8c4b0' }}>
                        <span className="font-bold" style={{ color: '#e8d5a3' }}>
                          {item.bold}
                        </span>{' '}
                        {item.rest}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-sm leading-relaxed mt-4" style={{ color: '#a8c4b0' }}>
                The course emphasizes that a law firm should not merely function as a stressful professional
                obligation, but as a strategically designed and sustainable business capable of supporting professional
                fulfillment, financial stability, operational health, and long-term freedom.
              </p>
            </div>
          </div>

          {/* Right Column */}
          <div className="flex flex-col gap-8">
            {/* Course Info Card */}
            <div
              className="rounded-2xl p-8"
              style={{ border: '1px solid rgba(201,168,76,0.3)', background: 'rgba(201,168,76,0.04)' }}
            >
              <h3 className="font-bold text-lg uppercase tracking-wide mb-5" style={{ color: '#C9A84C' }}>
                Proposed Course Information
              </h3>
              <div className="space-y-4">
                {[
                  { label: 'Instructor', value: 'Shirley E. Shepard, BS, MA, JD, LL.M.' },
                  { label: 'Course Type', value: 'Upper Division Elective' },
                  { label: 'Suggested Units', value: '2–3 Units' },
                  {
                    label: 'Format',
                    value:
                      'Lecture, Workshops, Strategic Planning Exercises, Operational Labs, Guest Speakers',
                  },
                  {
                    label: 'Target Audience',
                    value: '2L and 3L Law Students, LL.M. Candidates, New Attorneys, Emerging Solo Practitioners',
                  },
                ].map((row) => (
                  <div key={row.label} className="flex flex-col gap-1">
                    <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#C9A84C' }}>
                      {row.label}
                    </span>
                    <span className="text-sm" style={{ color: '#a8c4b0' }}>
                      {row.value}
                    </span>
                    <div
                      style={{ height: '1px', background: 'rgba(201,168,76,0.15)', marginTop: '4px' }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Four Pillars Summary */}
            <div>
              <h3 className="font-bold text-lg uppercase tracking-wide mb-2" style={{ color: '#C9A84C' }}>
                Four-Pillar Framework
              </h3>
              <p className="text-xs uppercase tracking-widest mb-5" style={{ color: '#e8d5a3' }}>
                Niche · Practice &amp; Procedures · Profit &amp; Growth · Exit Plan
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { emoji: '🎯', label: 'NICHE', desc: 'Identity & Alignment' },
                  { emoji: '⚙️', label: 'PRACTICE', desc: 'Operations & Systems' },
                  { emoji: '📈', label: 'PROFIT', desc: 'Growth & Sustainability' },
                  { emoji: '🚪', label: 'EXIT PLAN', desc: 'Succession & Control' },
                ].map((p) => (
                  <div
                    key={p.label}
                    className="rounded-lg p-4 flex flex-col items-center text-center gap-2 transition-all duration-300 hover:bg-opacity-10"
                    style={{ border: '1px solid rgba(201,168,76,0.3)', background: 'rgba(201,168,76,0.04)' }}
                  >
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                      style={{ border: '2px solid #C9A84C', background: 'rgba(201,168,76,0.1)' }}
                    >
                      {p.emoji}
                    </div>
                    <p className="text-xs font-black uppercase" style={{ color: '#C9A84C' }}>
                      {p.label}
                    </p>
                    <p className="text-xs" style={{ color: '#a8c4b0' }}>
                      {p.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Positioning Statement */}
            <div
              className="rounded-2xl p-6"
              style={{ background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.3)' }}
            >
              <p
                className="text-xs font-bold uppercase tracking-widest mb-3"
                style={{ color: '#C9A84C' }}
              >
                Proposed Course Positioning Statement
              </p>
              <p
                className="text-sm italic leading-relaxed"
                style={{ color: '#e8d5a3', fontFamily: "'Cormorant Garamond', serif", fontSize: '1rem' }}
              >
                &ldquo;Law school teaches students how to practice law. This course teaches students how to
                intentionally build, operate, sustain, and eventually transition a modern law practice with structure,
                profitability, operational clarity, and long-term control.&rdquo;
              </p>
            </div>
          </div>
        </div>
      </div>
      <GoldDivider thick />
    </section>
  );
}

// ─── Section: Four Pillars Grid ──────────────────────────────────────────────
function PillarsSection() {
  return (
    <section id="pillars" className="w-full" aria-labelledby="pillars-heading">
      <h2 id="pillars-heading" className="sr-only">
        Course Modules — Four-Pillar Framework
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 items-stretch">
        {pillars.map((pillar, idx) => (
          <div
            key={pillar.id}
            className="flex flex-col h-full"
            style={{
              background: '#0d2218',
              borderRight: idx < pillars.length - 1 ? '1px solid rgba(201,168,76,0.25)' : 'none',
            }}
          >
            <div className="flex-1 flex flex-col p-5 relative">
              {/* Background watermark */}
              <div
                className="absolute top-0 right-0 w-28 h-36 overflow-hidden"
                style={{ zIndex: 1, opacity: 0.18 }}
              >
                <div
                  className="absolute inset-0 flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, rgba(201,168,76,0.15), transparent)' }}
                >
                  <span style={{ fontSize: '4rem', color: '#C9A84C' }}>{pillar.emoji}</span>
                </div>
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      'linear-gradient(to right, #0d2218 0%, transparent 40%), linear-gradient(to top, #0d2218 0%, transparent 40%)',
                  }}
                />
              </div>

              {/* Heading */}
              <div className="relative z-10 mb-3" style={{ maxWidth: '75%' }}>
                <h3
                  className="font-black leading-none whitespace-pre-line"
                  style={{
                    color: '#C9A84C',
                    fontSize: 'clamp(1.3rem, 2.5vw, 1.8rem)',
                    fontFamily: "'Cormorant Garamond', serif",
                  }}
                >
                  {pillar.heading}
                </h3>
              </div>

              <p
                className="font-bold text-xs leading-snug mb-4 whitespace-pre-line relative z-10"
                style={{ color: '#e8d5a3', maxWidth: '75%' }}
              >
                {pillar.subheading}
              </p>

              {/* Items */}
              <div className="space-y-3 relative z-10 flex-1">
                {pillar.items.map((item) => (
                  <div key={item.title} className="flex items-start gap-2">
                    <IconCircle emoji={item.emoji} />
                    <div>
                      <p className="text-xs font-bold leading-tight" style={{ color: '#C9A84C' }}>
                        {item.title}
                      </p>
                      <p className="text-xs leading-snug mt-0.5" style={{ color: '#a8c4b0' }}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer bar — gold background like reference site */}
            <div className="px-4 py-3 flex items-start gap-3" style={{ background: '#C9A84C' }}>
              <span className="text-xl flex-shrink-0 mt-0.5">{pillar.footerEmoji}</span>
              <p
                className="text-xs font-semibold leading-snug whitespace-pre-line"
                style={{ color: '#0d2218' }}
              >
                {pillar.footerText}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Section: Course Objectives ──────────────────────────────────────────────
function ObjectivesSection() {
  return (
    <section style={{ background: '#0d2218' }}>
      <GoldDivider thick />
      <div
        className="w-full py-10 px-6 text-center"
        style={{ background: '#0a1c12', borderBottom: '1px solid rgba(201,168,76,0.2)' }}
      >
        <p className="text-xs uppercase tracking-widest mb-2" style={{ color: '#C9A84C' }}>
          What Students Will Learn
        </p>
        <h2
          className="font-black text-2xl md:text-3xl uppercase leading-tight"
          style={{ color: '#e8d5a3', fontFamily: "'Cormorant Garamond', serif" }}
        >
          Course Learning Objectives
        </h2>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {objectives.map((obj, i) => (
            <div
              key={i}
              className="flex items-start gap-3 rounded-xl p-4 transition-all duration-300"
              style={{ border: '1px solid rgba(201,168,76,0.2)', background: 'rgba(201,168,76,0.04)' }}
            >
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-black"
                style={{ background: '#C9A84C', color: '#0d2218' }}
              >
                {i + 1}
              </div>
              <p className="text-sm leading-relaxed" style={{ color: '#a8c4b0' }}>
                {obj.text}
              </p>
            </div>
          ))}
        </div>
      </div>
      <GoldDivider thick />
    </section>
  );
}

// ─── Section: Detailed Modules ───────────────────────────────────────────────
function ModulesSection() {
  const [openModule, setOpenModule] = useState<string | null>(null);

  return (
    <section id="modules" style={{ background: '#0d2218' }}>
      <div
        className="w-full py-10 px-6 text-center"
        style={{ background: '#0a1c12', borderBottom: '1px solid rgba(201,168,76,0.2)' }}
      >
        <p className="text-xs uppercase tracking-widest mb-2" style={{ color: '#C9A84C' }}>
          Proposed Course Structure
        </p>
        <h2
          className="font-black text-2xl md:text-3xl uppercase leading-tight"
          style={{ color: '#e8d5a3', fontFamily: "'Cormorant Garamond', serif" }}
        >
          Course Modules
        </h2>
        <p className="text-sm mt-2" style={{ color: '#a8c4b0' }}>
          Click each module to expand full details
        </p>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex flex-col gap-4">
          {modules.map((mod) => {
            const isOpen = openModule === mod.number;
            return (
              <div
                key={mod.number}
                className="rounded-xl overflow-hidden"
                style={{ border: '1px solid rgba(201,168,76,0.3)', background: 'rgba(201,168,76,0.04)' }}
              >
                <button
                  className="w-full flex items-center justify-between px-6 py-5 text-left transition-all duration-300"
                  style={{ background: isOpen ? 'rgba(201,168,76,0.1)' : 'transparent' }}
                  onClick={() => setOpenModule(isOpen ? null : mod.number)}
                  aria-expanded={isOpen}
                >
                  <span className="flex items-center gap-4">
                    <span
                      className="w-10 h-10 rounded-full flex items-center justify-center text-xl flex-shrink-0"
                      style={{ border: '2px solid #C9A84C', background: 'rgba(201,168,76,0.1)' }}
                    >
                      {mod.emoji}
                    </span>
                    <span>
                      <span
                        className="block text-xs font-bold uppercase tracking-widest"
                        style={{ color: '#a8c4b0' }}
                      >
                        Module {mod.number}
                      </span>
                      <span
                        className="font-black text-base uppercase"
                        style={{ color: '#C9A84C', fontFamily: "'Cormorant Garamond', serif" }}
                      >
                        {mod.title}
                      </span>
                      <span className="block text-xs mt-0.5" style={{ color: '#a8c4b0' }}>
                        {mod.subtitle}
                      </span>
                    </span>
                  </span>
                  <span
                    style={{ color: '#C9A84C', fontSize: '22px', lineHeight: 1, flexShrink: 0 }}
                  >
                    {isOpen ? '−' : '+'}
                  </span>
                </button>

                {isOpen && (
                  <div className="px-6 pb-6">
                    <div
                      style={{ height: '1px', background: 'rgba(201,168,76,0.2)', marginBottom: '1.5rem' }}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Topics */}
                      <div>
                        <p
                          className="text-xs font-bold uppercase tracking-wide mb-3"
                          style={{ color: '#C9A84C' }}
                        >
                          Topics Include:
                        </p>
                        <ul className="space-y-1.5">
                          {mod.topics.map((t) => (
                            <BulletItem key={t} text={t} />
                          ))}
                        </ul>
                      </div>

                      <div className="flex flex-col gap-5">
                        {/* Assignments */}
                        <div>
                          <p
                            className="text-xs font-bold uppercase tracking-wide mb-3"
                            style={{ color: '#C9A84C' }}
                          >
                            Assignments:
                          </p>
                          <ul className="space-y-1.5">
                            {mod.assignments.map((a) => (
                              <BulletItem key={a} text={a} />
                            ))}
                          </ul>
                        </div>

                        {/* Tech Platforms */}
                        {mod.tech && (
                          <div>
                            <p
                              className="text-xs font-bold uppercase tracking-wide mb-3"
                              style={{ color: '#C9A84C' }}
                            >
                              Technology Platforms:
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {mod.tech.map((t) => (
                                <span
                                  key={t}
                                  className="px-2 py-1 rounded text-xs font-bold"
                                  style={{
                                    background: 'rgba(201,168,76,0.15)',
                                    color: '#C9A84C',
                                    border: '1px solid rgba(201,168,76,0.3)',
                                  }}
                                >
                                  {t}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <GoldDivider thick />
    </section>
  );
}

// ─── Section: Final Project ──────────────────────────────────────────────────
function FinalProjectSection() {
  const deliverables = [
    'Niche identification',
    'Operational systems',
    'Workflow procedures',
    'Technology recommendations',
    'Profitability strategy',
    'Sustainability planning',
    'Exit and transition considerations',
  ];

  const methodology = [
    {
      emoji: '🎓',
      title: 'Lecture-Based Instruction',
      desc: 'Real-world litigation operations experience and organizational consulting principles',
    },
    {
      emoji: '🔧',
      title: 'Operational Workshops',
      desc: 'Interactive labs working through practical scenarios reflecting real attorney challenges',
    },
    {
      emoji: '📋',
      title: 'Strategic Planning Exercises',
      desc: 'Case-study analysis and strategic planning exercises for modern law practice',
    },
    {
      emoji: '💻',
      title: 'Legal Technology Demonstrations',
      desc: 'Hands-on evaluation of AI-assisted tools and case management platforms',
    },
    {
      emoji: '🎤',
      title: 'Guest Speakers',
      desc: 'Professionals from legal operations, AI, and law firm management sectors',
    },
  ];

  return (
    <section style={{ background: '#0d2218' }}>
      <div
        className="w-full py-10 px-6 text-center"
        style={{ background: '#0a1c12', borderBottom: '1px solid rgba(201,168,76,0.2)' }}
      >
        <p className="text-xs uppercase tracking-widest mb-2" style={{ color: '#C9A84C' }}>
          Capstone Assignment
        </p>
        <h2
          className="font-black text-2xl md:text-3xl uppercase leading-tight"
          style={{ color: '#e8d5a3', fontFamily: "'Cormorant Garamond', serif" }}
        >
          Final Course Project
        </h2>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Final Project */}
          <div>
            <div
              className="rounded-2xl p-8 mb-6"
              style={{ border: '1px solid rgba(201,168,76,0.3)', background: 'rgba(201,168,76,0.07)' }}
            >
              <p
                className="text-xs font-bold uppercase tracking-widest mb-2"
                style={{ color: '#C9A84C' }}
              >
                Students Will Develop a Comprehensive:
              </p>
              <h3
                className="font-black text-2xl uppercase mb-4"
                style={{ color: '#e8d5a3', fontFamily: "'Cormorant Garamond', serif" }}
              >
                &ldquo;Strategic Law Firm Blueprint&rdquo;
              </h3>
              <p className="text-sm leading-relaxed mb-5" style={{ color: '#a8c4b0' }}>
                The final project is designed to function as a foundational operational business plan for a modern
                legal practice, integrating all four course pillars into a cohesive, actionable document.
              </p>
              <p
                className="text-xs font-bold uppercase tracking-wide mb-3"
                style={{ color: '#C9A84C' }}
              >
                The Final Project Integrates:
              </p>
              <ul className="space-y-2">
                {deliverables.map((d) => (
                  <BulletItem key={d} text={d} />
                ))}
              </ul>
            </div>

            {/* Suggested Resources */}
            <div
              className="rounded-xl p-6"
              style={{ border: '1px solid rgba(201,168,76,0.2)', background: 'rgba(201,168,76,0.04)' }}
            >
              <p
                className="text-xs font-bold uppercase tracking-wide mb-3"
                style={{ color: '#C9A84C' }}
              >
                Suggested Reading &amp; Resources
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  'Legal Operations Management',
                  'Attorney Wellness & Burnout Prevention',
                  'Law Firm Economics',
                  'AI & Legal Technology',
                  'Organizational Consulting Principles',
                  'Workflow Optimization',
                  'Law Practice Management',
                  'Strategic Leadership',
                ].map((r) => (
                  <span
                    key={r}
                    className="px-3 py-1.5 rounded-full text-xs"
                    style={{
                      background: 'rgba(201,168,76,0.1)',
                      color: '#a8c4b0',
                      border: '1px solid rgba(201,168,76,0.2)',
                    }}
                  >
                    {r}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Teaching Methodology */}
          <div>
            <h3
              className="font-bold text-lg uppercase tracking-wide mb-5"
              style={{ color: '#C9A84C' }}
            >
              Teaching Methodology
            </h3>
            <div className="space-y-4">
              {methodology.map((m) => (
                <div
                  key={m.title}
                  className="flex items-start gap-4 rounded-xl p-4 transition-all duration-300"
                  style={{ border: '1px solid rgba(201,168,76,0.2)', background: 'rgba(201,168,76,0.04)' }}
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-xl flex-shrink-0"
                    style={{
                      background: 'rgba(201,168,76,0.15)',
                      border: '1px solid rgba(201,168,76,0.3)',
                    }}
                  >
                    {m.emoji}
                  </div>
                  <div>
                    <p className="text-sm font-bold" style={{ color: '#e8d5a3' }}>
                      {m.title}
                    </p>
                    <p className="text-xs leading-relaxed mt-1" style={{ color: '#a8c4b0' }}>
                      {m.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <GoldDivider thick />
    </section>
  );
}

// ─── Section: Instructor Bio ─────────────────────────────────────────────────
function InstructorSection() {
  const education = [
    { degree: 'LL.M.', field: 'Small Business Operations', school: 'Concord Law School' },
    { degree: 'M.A.', field: 'Organizational Consulting', school: 'Phillips Graduate Institute' },
    { degree: 'J.D.', field: 'Law Studies', school: 'University of West Los Angeles' },
    { degree: 'B.S.', field: 'Criminal Justice', school: 'Cal State Los Angeles' },
    { degree: 'Paralegal Cert.', field: 'Paralegal Studies', school: 'Waterson College' },
  ];

  const focus = [
    'Legal operations',
    'AI-assisted legal workflow systems',
    'Case management infrastructure',
    'Strategic law firm development',
    'Workflow optimization',
    'Operational sustainability',
    'Long-term practice design',
  ];

  return (
    <section id="instructor" style={{ background: '#0d2218' }}>
      <div
        className="w-full py-10 px-6 text-center"
        style={{ background: '#0a1c12', borderBottom: '1px solid rgba(201,168,76,0.2)' }}
      >
        <p className="text-xs uppercase tracking-widest mb-2" style={{ color: '#C9A84C' }}>
          Course Instructor
        </p>
        <h2
          className="font-black text-2xl md:text-3xl uppercase leading-tight"
          style={{ color: '#e8d5a3', fontFamily: "'Cormorant Garamond', serif" }}
        >
          About Shirley E. Shepard
        </h2>
        <p
          className="text-sm mt-2 font-semibold uppercase tracking-wide"
          style={{ color: '#C9A84C' }}
        >
          Founder, NPPLF-Law School Course
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Left: Portrait + taglines + education */}
          <div className="flex flex-col items-center lg:sticky lg:top-24">
            {/* Founder Photo — replace src with actual founder image */}
            <div
              className="rounded-2xl overflow-hidden"
              style={{
                border: '2px solid #C9A84C',
                boxShadow: '0 0 40px rgba(201,168,76,0.25)',
                width: '100%',
                maxWidth: '320px',
                aspectRatio: '3/4',
                position: 'relative',
                background: 'linear-gradient(135deg, #0a1c12 0%, #0d2218 60%, #0a1c12 100%)',
              }}
            >
              <img
                src="/assets/images/Screenshot_2026-05-16_203005-1779159125021.png"
                alt="Shirley E. Shepard, Founder of NPPLF-Law School Course, at her desk with NPP Law Firm Consulting branding"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              {/* Name overlay removed */}
            </div>

            {/* Education Card */}
            <div
              className="mt-6 w-full max-w-sm rounded-xl p-5"
              style={{ background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.25)' }}
            >
              <p
                className="font-bold text-sm uppercase tracking-wide mb-4 text-center"
                style={{ color: '#C9A84C' }}
              >
                Shirley E. Shepard&rsquo;s Education
              </p>
              <div className="space-y-3">
                {education.map((e) => (
                  <div key={e.degree} className="flex items-start gap-3">
                    <span
                      className="px-2 py-0.5 rounded text-xs font-black flex-shrink-0 mt-0.5"
                      style={{
                        background: 'rgba(201,168,76,0.2)',
                        color: '#C9A84C',
                        minWidth: '3rem',
                        textAlign: 'center',
                      }}
                    >
                      {e.degree}
                    </span>
                    <div className="flex flex-col">
                      <p className="text-xs font-bold leading-snug" style={{ color: '#e8d5a3' }}>
                        {e.field}
                      </p>
                      <p className="text-xs leading-snug" style={{ color: '#a8c4b0' }}>
                        {e.school}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Bio text */}
          <div className="flex flex-col gap-6">
            <div>
              <h3
                className="font-bold text-lg uppercase tracking-wide mb-4"
                style={{ color: '#C9A84C' }}
              >
                About NPPLF-Law School Course
              </h3>
              <p className="text-sm leading-relaxed mb-4" style={{ color: '#a8c4b0' }}>
                Our consulting philosophy is centered on guiding attorneys in building firms they genuinely enjoy
                leading by creating practices aligned with their strengths, interests, passions, lifestyle goals, and
                long-term vision.
              </p>
              <p className="text-sm leading-relaxed" style={{ color: '#a8c4b0' }}>
                At NPPLF-Law School Course, we view a law firm not as a static professional practice, but as a living and
                evolving business model requiring continuous operational review, income analysis, system refinement,
                technology adaptation, and strategic planning designed to support both present profitability and
                long-term stability.
              </p>
            </div>

            <GoldDivider />

            <div>
              <h3
                className="font-bold text-lg uppercase tracking-wide mb-4"
                style={{ color: '#C9A84C' }}
              >
                Instructor Biography
              </h3>
              <p className="text-sm leading-relaxed mb-4" style={{ color: '#a8c4b0' }}>
                Shirley E. Shepard, BS, MA, JD, LL.M. is the founder of NPPLF-Law School Course and brings more than 20 years of litigation and legal
                operations leadership experience across plaintiff and defense law firms. Her multidisciplinary
                educational background includes a Master of Laws (LL.M.) in Small Business Operations from Concord
                Law School, a Master of Arts in Organizational Consulting from Phillips Graduate Institute, a Juris
                Doctor in Law Studies from the University of West Los Angeles, a Bachelor of Science in Criminal
                Justice from California State University Los Angeles, and a Paralegal Certificate in Paralegal Studies
                from Waterson College.
              </p>
              <p className="text-sm leading-relaxed" style={{ color: '#a8c4b0' }}>
                NPPLF-Law School Course was created from both professional experience and a genuine passion for the long-term
                health, sustainability, and evolution of the legal profession. After decades working inside
                high-pressure litigation environments, Shirley E. Shepard, BS, MA, JD, LL.M. developed a deep understanding of the operational
                challenges, burnout, inefficiencies, and structural demands attorneys and law firms face on a daily
                basis. That experience shaped a consulting philosophy centered on building firms that operate with
                greater clarity, structure, purpose, profitability, and balance.
              </p>
            </div>

            <GoldDivider />

            {/* Professional Focus */}
            <div>
              <h3
                className="font-bold text-lg uppercase tracking-wide mb-4"
                style={{ color: '#C9A84C' }}
              >
                Professional Focus
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {focus.map((f) => (
                  <div key={f} className="flex items-start gap-2">
                    <span
                      style={{ color: '#C9A84C', fontSize: '10px', marginTop: '4px', flexShrink: 0 }}
                    >
                      ✓
                    </span>
                    <span className="text-sm" style={{ color: '#a8c4b0' }}>
                      {f}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <GoldDivider />

            {/* Positioning Statement */}
            <div
              className="rounded-2xl p-6"
              style={{ background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.3)' }}
            >
              <p
                className="font-black text-sm uppercase tracking-widest mb-4 text-center"
                style={{ color: '#C9A84C' }}
              >
                Niche. Practice. Profit. Exit Plan.
              </p>
              <p
                className="text-sm italic text-center leading-relaxed"
                style={{ color: '#e8d5a3', fontFamily: "'Cormorant Garamond', serif", fontSize: '1rem' }}
              >
                Build a law firm designed to support the future of the attorney — not consume it.
              </p>
              <a
                href="#contact"
                className="block text-center font-bold text-sm uppercase tracking-widest py-4 px-6 rounded mt-6 transition-opacity hover:opacity-90"
                style={{ background: '#C9A84C', color: '#0a1c12' }}
              >
                Submit Course Proposal Inquiry
              </a>
            </div>
          </div>
        </div>
      </div>
      <GoldDivider thick />
    </section>
  );
}

// ─── Section: CTA Banner ─────────────────────────────────────────────────────
function CTABanner() {
  return (
    <section
      className="w-full"
      style={{ background: '#0d2218' }}
      aria-labelledby="cta-heading"
    >
      <GoldDivider thick />
      <div className="w-full flex flex-col items-center justify-center py-16 px-6 text-center">
          <h2
            id="cta-heading"
            className="font-bold text-2xl md:text-3xl leading-snug mb-8 text-center max-w-4xl mx-auto"
            style={{ color: '#e8d5a3' }}
          >
            BRIDGE THE GAP BETWEEN LEGAL EDUCATION AND{' '}
            <span
              className="font-black uppercase"
              style={{ color: '#C9A84C' }}
            >
              THE OPERATIONAL REALITIES OF MODERN LEGAL PRACTICE.
            </span>
          </h2>

          <div className="grid grid-cols-4 gap-6 mb-10 w-full max-w-3xl mx-auto">
            {[
              { emoji: '🎯', label: 'NICHE' },
              { emoji: '⚙️', label: 'PRACTICE &\nPROCEDURES' },
              { emoji: '📈', label: 'PROFIT' },
              { emoji: '🚪', label: 'EXIT PLAN\n& CONTROL' },
            ].map((p) => (
              <div key={p.label} className="flex flex-col items-center text-center gap-2">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center text-3xl"
                  style={{ border: '2px solid #C9A84C', background: 'rgba(201,168,76,0.1)' }}
                >
                  {p.emoji}
                </div>
                <p
                  className="text-xs font-bold uppercase leading-tight whitespace-pre-line"
                  style={{ color: '#e8d5a3' }}
                >
                  {p.label}
                </p>
              </div>
            ))}
          </div>

          <p className="text-base leading-relaxed mb-8 max-w-2xl mx-auto" style={{ color: '#a8c4b0' }}>
            NPPLF-Law School Course respectfully proposes this innovative elective course to help future attorneys build
            structured, profitable, and sustainable law practices aligned with their strengths, interests, and
            long-term vision.
          </p>

          <a
            href="#contact"
            className="inline-block text-center font-bold text-sm uppercase tracking-widest py-4 px-10 rounded transition-opacity hover:opacity-90"
            style={{ border: '2px solid #C9A84C', color: '#0d2218', background: '#C9A84C' }}
          >
            LAW SCHOOL TEACHES HOW TO PRACTICE LAW.
            <br />
            THIS COURSE TEACHES HOW TO BUILD A LAW FIRM.
          </a>
      </div>
    </section>
  );
}

// ─── Section: Contact / Submit Proposal ─────────────────────────────────────
function ContactSection() {
  const TOTAL_STEPS = 4;
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    role: '',
    roleOther: '',
    institution: '',
    institutionType: '',
    inquiryType: '',
    inquiryDetail: '',
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const inputStyle = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(201,168,76,0.3)',
    color: '#e8d5a3',
  };

  const labelClass = 'block text-xs font-bold uppercase tracking-[0.15em] mb-1.5';

  const roleOptions = [
    { value: 'dean', label: 'Dean' },
    { value: 'professor', label: 'Professor / Faculty' },
    { value: 'committee_member', label: 'Curriculum Committee Member' },
    { value: 'administrator', label: 'Academic Administrator' },
    { value: 'student', label: 'Law Student' },
    { value: 'attorney', label: 'Practicing Attorney' },
    { value: 'other', label: 'Other' },
  ];

  const inquiryOptions = [
    { value: 'enrollment', label: '📋 Course Enrollment', desc: 'Adopt or integrate this course into your curriculum' },
    { value: 'curriculum', label: '📚 Curriculum Discussion', desc: 'Explore how this course fits your program' },
    { value: 'speaking', label: '🎤 Speaking Engagement', desc: 'Invite Shirley E. Shepard, BS, MA, JD, LL.M. to speak at your institution' },
    { value: 'workshop', label: '⚙️ Workshop / Seminar', desc: 'Host a standalone workshop or intensive session' },
    { value: 'partnership', label: '🤝 Partnership Inquiry', desc: 'Explore co-development or licensing opportunities' },
    { value: 'general', label: '💬 General Inquiry', desc: 'Other questions about the course or program' },
  ];

  const stepLabels = ['Your Role', 'Institution', 'Inquiry Type', 'Contact Details'];

  return (
    <section id="contact" aria-labelledby="contact-heading">
      <div className="py-16 px-6" style={{ background: '#0d2218' }}>
        <div
          style={{
            height: '1px',
            background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)',
            marginBottom: '48px',
          }}
        />
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <p
              className="text-xs font-bold uppercase tracking-[0.35em] mb-3"
              style={{ color: '#C9A84C' }}
            >
              Get In Touch
            </p>
            <h2
              id="contact-heading"
              className="font-black text-3xl md:text-4xl uppercase mb-4"
              style={{ color: '#C9A84C', fontFamily: "'Cormorant Garamond', serif" }}
            >
              Submit a Course Proposal Inquiry
            </h2>
            <p className="text-sm max-w-xl mx-auto" style={{ color: '#a8c4b0' }}>
              For curriculum discussions, speaking engagements, workshops, or course development inquiries, please
              contact NPPLF-Law School Course.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-10 items-start">
            {/* Left: Contact Info */}
            <div className="space-y-4">
              <p
                className="text-xs font-bold uppercase tracking-[0.2em] mb-4"
                style={{ color: '#C9A84C' }}
              >
                Reach Out Directly
              </p>

              <a
                href="sms:+18184453897?body=Hi%2C%20I%27d%20like%20to%20discuss%20the%20NPPLF%20course%20proposal."
                className="flex items-center gap-4 rounded-xl px-6 py-4 transition-all duration-300 group"
                style={{ border: '1px solid rgba(201,168,76,0.3)', background: 'rgba(201,168,76,0.05)' }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(201,168,76,0.15)' }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="#C9A84C"
                    width={20}
                    height={20}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-bold" style={{ color: '#e8d5a3' }}>
                    Send a Text
                  </div>
                  <div className="text-xs" style={{ color: '#a8c4b0' }}>
                    (818) 445-3897
                  </div>
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="#a8c4b0"
                  width={16}
                  height={16}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                  />
                </svg>
              </a>

              <a
                href="mailto:SShepard@NPPLFconsulting.com?subject=Course%20Proposal%20Inquiry"
                className="flex items-center gap-4 rounded-xl px-6 py-4 transition-all duration-300 group"
                style={{ border: '1px solid rgba(201,168,76,0.3)', background: 'rgba(201,168,76,0.05)' }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(201,168,76,0.15)' }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="#C9A84C"
                    width={20}
                    height={20}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-bold" style={{ color: '#e8d5a3' }}>
                    Send an Email
                  </div>
                  <div className="text-xs" style={{ color: '#a8c4b0' }}>
                    SShepard@NPPLFconsulting.com
                  </div>
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="#a8c4b0"
                  width={16}
                  height={16}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                  />
                </svg>
              </a>

              <div className="mt-6 space-y-3">
                <CheckItem text="Discuss curriculum structure and course integration" />
                <CheckItem text="Explore guest speaker and workshop opportunities" />
                <CheckItem text="Review the full proposed course outline and overview" />
                <CheckItem text="Schedule a confidential consultation with Shirley E. Shepard, BS, MA, JD, LL.M." />
              </div>

              {/* Website link */}
              <div
                className="mt-6 rounded-xl p-4 flex items-center gap-3"
                style={{ border: '1px solid rgba(201,168,76,0.3)', background: 'rgba(201,168,76,0.05)' }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="#C9A84C"
                  width={20}
                  height={20}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"
                  />
                </svg>
                <a
                  href="https://NPPLFconsulting.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-base"
                  style={{ color: '#C9A84C', fontFamily: "'Cormorant Garamond', serif" }}
                >
                  NPPLFconsulting.com
                </a>
              </div>
            </div>

            {/* Right: Multi-Step Form */}
            <div
              className="rounded-2xl p-8"
              style={{ border: '1px solid rgba(201,168,76,0.25)', background: 'rgba(201,168,76,0.04)' }}
            >
              {submitted ? (
                <div className="text-center py-8">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-3xl mx-auto mb-4"
                    style={{ background: 'rgba(201,168,76,0.15)', border: '2px solid #C9A84C' }}
                  >
                    ✓
                  </div>
                  <h3
                    className="font-black text-xl uppercase mb-2"
                    style={{ color: '#C9A84C', fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    Inquiry Submitted
                  </h3>
                  <p className="text-sm mb-6" style={{ color: '#a8c4b0' }}>
                    Thank you for your interest. Shirley E. Shepard, BS, MA, JD, LL.M. will be in touch shortly to discuss the course proposal.
                  </p>
                  {/* Summary */}
                  <div className="text-left rounded-xl p-4 space-y-2" style={{ border: '1px solid rgba(201,168,76,0.2)', background: 'rgba(201,168,76,0.04)' }}>
                    {[
                      { label: 'Role', value: formData.role === 'other' ? formData.roleOther : roleOptions.find(r => r.value === formData.role)?.label },
                      { label: 'Institution', value: formData.institution },
                      { label: 'Inquiry', value: inquiryOptions.find(i => i.value === formData.inquiryType)?.label },
                      { label: 'Contact', value: formData.email },
                    ].map(({ label, value }) =>
                      value ? (
                        <div key={label} className="flex gap-2 text-xs">
                          <span className="font-bold uppercase tracking-wide flex-shrink-0" style={{ color: '#C9A84C', minWidth: '70px' }}>{label}:</span>
                          <span style={{ color: '#a8c4b0' }}>{value}</span>
                        </div>
                      ) : null
                    )}
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate>
                  {/* Step Progress Bar */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      {stepLabels.map((label, idx) => {
                        const stepNum = idx + 1;
                        const isActive = stepNum === step;
                        const isDone = stepNum < step;
                        return (
                          <div key={label} className="flex flex-col items-center gap-1" style={{ flex: 1 }}>
                            <div
                              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all duration-300"
                              style={{
                                background: isDone ? '#C9A84C' : isActive ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.05)',
                                border: isActive ? '2px solid #C9A84C' : isDone ? '2px solid #C9A84C' : '1px solid rgba(201,168,76,0.2)',
                                color: isDone ? '#0d2218' : isActive ? '#C9A84C' : '#a8c4b0',
                              }}
                            >
                              {isDone ? '✓' : stepNum}
                            </div>
                            <span
                              className="text-center leading-tight"
                              style={{
                                color: isActive ? '#C9A84C' : isDone ? '#e8d5a3' : '#a8c4b0',
                                fontSize: '9px',
                                fontWeight: isActive ? 700 : 400,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                              }}
                            >
                              {label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {/* Progress line */}
                    <div className="relative h-0.5 rounded-full mx-3" style={{ background: 'rgba(201,168,76,0.15)' }}>
                      <div
                        className="absolute left-0 top-0 h-full rounded-full transition-all duration-500"
                        style={{ background: '#C9A84C', width: `${((step - 1) / (TOTAL_STEPS - 1)) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Step 1: Role */}
                  {step === 1 && (
                    <div>
                      <h3 className="text-base font-black uppercase mb-1" style={{ color: '#e8d5a3', fontFamily: "'Cormorant Garamond', serif" }}>
                        Your Role
                      </h3>
                      <p className="text-xs mb-5" style={{ color: '#a8c4b0' }}>
                        Help us understand your position so we can tailor our response.
                      </p>
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        {roleOptions.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setFormData({ ...formData, role: opt.value })}
                            className="px-3 py-3 rounded-lg text-xs font-bold uppercase tracking-wide text-left transition-all duration-200"
                            style={{
                              border: formData.role === opt.value ? '2px solid #C9A84C' : '1px solid rgba(201,168,76,0.25)',
                              background: formData.role === opt.value ? 'rgba(201,168,76,0.15)' : 'rgba(255,255,255,0.03)',
                              color: formData.role === opt.value ? '#C9A84C' : '#a8c4b0',
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {formData.role === 'other' && (
                        <div className="mt-3">
                          <label htmlFor="roleOther" className={labelClass} style={{ color: '#C9A84C' }}>
                            Please specify *
                          </label>
                          <input
                            id="roleOther"
                            name="roleOther"
                            type="text"
                            placeholder="Your role or title"
                            value={formData.roleOther}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg text-sm outline-none"
                            style={inputStyle}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 2: Institution */}
                  {step === 2 && (
                    <div>
                      <h3 className="text-base font-black uppercase mb-1" style={{ color: '#e8d5a3', fontFamily: "'Cormorant Garamond', serif" }}>
                        Your Institution
                      </h3>
                      <p className="text-xs mb-5" style={{ color: '#a8c4b0' }}>
                        Tell us about your law school or organization.
                      </p>
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="institution" className={labelClass} style={{ color: '#C9A84C' }}>
                            Institution / Law School *
                          </label>
                          <input
                            id="institution"
                            name="institution"
                            type="text"
                            required
                            placeholder="e.g. UCLA School of Law"
                            value={formData.institution}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg text-sm outline-none"
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label htmlFor="institutionType" className={labelClass} style={{ color: '#C9A84C' }}>
                            Institution Type
                          </label>
                          <select
                            id="institutionType"
                            name="institutionType"
                            value={formData.institutionType}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg text-sm outline-none"
                            style={{ ...inputStyle, appearance: 'none' }}
                          >
                            <option value="" style={{ background: '#0d2218' }}>Select type...</option>
                            <option value="aba_law_school" style={{ background: '#0d2218' }}>ABA-Accredited Law School</option>
                            <option value="state_law_school" style={{ background: '#0d2218' }}>State Bar-Accredited Law School</option>
                            <option value="university" style={{ background: '#0d2218' }}>University / College</option>
                            <option value="bar_association" style={{ background: '#0d2218' }}>Bar Association</option>
                            <option value="law_firm" style={{ background: '#0d2218' }}>Law Firm</option>
                            <option value="other" style={{ background: '#0d2218' }}>Other</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Inquiry Type */}
                  {step === 3 && (
                    <div>
                      <h3 className="text-base font-black uppercase mb-1" style={{ color: '#e8d5a3', fontFamily: "'Cormorant Garamond', serif" }}>
                        Nature of Inquiry
                      </h3>
                      <p className="text-xs mb-5" style={{ color: '#a8c4b0' }}>
                        What best describes the purpose of your outreach?
                      </p>
                      <div className="space-y-2 mb-4">
                        {inquiryOptions.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setFormData({ ...formData, inquiryType: opt.value })}
                            className="w-full px-4 py-3 rounded-lg text-left transition-all duration-200 flex items-start gap-3"
                            style={{
                              border: formData.inquiryType === opt.value ? '2px solid #C9A84C' : '1px solid rgba(201,168,76,0.2)',
                              background: formData.inquiryType === opt.value ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.03)',
                            }}
                          >
                            <div className="flex-1">
                              <div className="text-xs font-bold uppercase tracking-wide" style={{ color: formData.inquiryType === opt.value ? '#C9A84C' : '#e8d5a3' }}>
                                {opt.label}
                              </div>
                              <div className="text-xs mt-0.5" style={{ color: '#a8c4b0' }}>{opt.desc}</div>
                            </div>
                            {formData.inquiryType === opt.value && (
                              <div className="w-4 h-4 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center" style={{ background: '#C9A84C' }}>
                                <span style={{ color: '#0d2218', fontSize: '9px', fontWeight: 900 }}>✓</span>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                      {formData.inquiryType && (
                        <div>
                          <label htmlFor="inquiryDetail" className={labelClass} style={{ color: '#C9A84C' }}>
                            Additional Details
                          </label>
                          <textarea
                            id="inquiryDetail"
                            name="inquiryDetail"
                            rows={2}
                            placeholder="Any specific questions or context..."
                            value={formData.inquiryDetail}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg text-sm outline-none resize-none"
                            style={inputStyle}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 4: Contact Details */}
                  {step === 4 && (
                    <div>
                      <h3 className="text-base font-black uppercase mb-1" style={{ color: '#e8d5a3', fontFamily: "'Cormorant Garamond', serif" }}>
                        Contact Details
                      </h3>
                      <p className="text-xs mb-5" style={{ color: '#a8c4b0' }}>
                        How should Shirley E. Shepard, BS, MA, JD, LL.M. reach you?
                      </p>
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="name" className={labelClass} style={{ color: '#C9A84C' }}>
                            Full Name *
                          </label>
                          <input
                            id="name"
                            name="name"
                            type="text"
                            required
                            placeholder="Your full name"
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg text-sm outline-none"
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label htmlFor="email" className={labelClass} style={{ color: '#C9A84C' }}>
                            Email Address *
                          </label>
                          <input
                            id="email"
                            name="email"
                            type="email"
                            required
                            placeholder="your@email.com"
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg text-sm outline-none"
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label htmlFor="phone" className={labelClass} style={{ color: '#C9A84C' }}>
                            Phone Number
                          </label>
                          <input
                            id="phone"
                            name="phone"
                            type="tel"
                            placeholder="(555) 000-0000"
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg text-sm outline-none"
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label htmlFor="message" className={labelClass} style={{ color: '#C9A84C' }}>
                            Message
                          </label>
                          <textarea
                            id="message"
                            name="message"
                            rows={3}
                            placeholder="Anything else you'd like us to know..."
                            value={formData.message}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg text-sm outline-none resize-none"
                            style={inputStyle}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex gap-3 mt-6">
                    {step > 1 && (
                      <button
                        type="button"
                        onClick={handleBack}
                        className="flex-1 py-3 rounded-lg font-bold text-xs uppercase tracking-widest transition-all duration-300"
                        style={{
                          border: '1px solid rgba(201,168,76,0.4)',
                          background: 'transparent',
                          color: '#C9A84C',
                        }}
                      >
                        ← Back
                      </button>
                    )}
                    {step < TOTAL_STEPS ? (
                      <button
                        type="button"
                        onClick={handleNext}
                        disabled={
                          (step === 1 && !formData.role) ||
                          (step === 1 && formData.role === 'other' && !formData.roleOther) ||
                          (step === 2 && !formData.institution) ||
                          (step === 3 && !formData.inquiryType)
                        }
                        className="flex-1 py-3 rounded-lg font-bold text-xs uppercase tracking-widest transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ background: '#C9A84C', color: '#0d2218' }}
                      >
                        Continue →
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={!formData.name || !formData.email}
                        className="flex-1 py-3 rounded-lg font-bold text-xs uppercase tracking-widest transition-all duration-300 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ background: '#C9A84C', color: '#0d2218' }}
                      >
                        Submit Inquiry
                      </button>
                    )}
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="w-full" style={{ background: '#0a1c12', borderTop: '2px solid #C9A84C' }}>
        <div className="max-w-7xl mx-auto px-6 py-5 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          {/* Left */}
          <div className="flex items-start gap-4">
            <div
              className="w-12 h-12 rounded flex items-center justify-center flex-shrink-0"
              style={{ border: '1px solid #C9A84C' }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="#C9A84C"
                width={24}
                height={24}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 3.741-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5"
                />
              </svg>
            </div>
            <div>
              <p
                className="font-bold text-sm uppercase tracking-wide"
                style={{ color: '#C9A84C' }}
              >
                NPPLF-Law School Course
              </p>
              <p className="text-xs" style={{ color: '#a8c4b0' }}>
                Building law firms designed to support
                <br />
                the future of the attorney — not consume it.
              </p>
            </div>
          </div>

          {/* Center */}
          <div className="flex flex-col items-center text-center">
            <a
              href="https://NPPLFconsulting.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-xl"
              style={{ color: '#C9A84C', fontFamily: "'Cormorant Garamond', serif" }}
            >
              NPPLFconsulting.com
            </a>
            <p className="text-xs mt-1" style={{ color: '#a8c4b0' }}>
              Niche. Practice. Profit. Exit Plan.
            </p>
          </div>

          {/* Right */}
          <div className="flex items-center justify-end gap-4">
            <div className="text-right">
              <p className="text-xs font-bold uppercase" style={{ color: '#C9A84C' }}>
                Proposed California
              </p>
              <p className="text-xs font-bold uppercase" style={{ color: '#C9A84C' }}>
                Law School Elective
              </p>
              <p className="text-xs" style={{ color: '#a8c4b0' }}>
                Upper Division · 2–3 Units
              </p>
            </div>
            <div
              className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0"
              style={{ border: '2px solid #C9A84C', boxShadow: '0 0 12px rgba(201,168,76,0.3)' }}
            >
              <img
                src="/assets/images/Screenshot_2026-05-16_203005-1779159125021.png"
                alt="Shirley E. Shepard, Founder of NPPLF-Law School Course"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
          </div>
        </div>
        <div
          className="text-center py-2 text-xs"
          style={{ color: '#a8c4b0', borderTop: '1px solid rgba(201,168,76,0.15)' }}
        >
          © 2025 NPPLF-Law School Course. All rights reserved. | Shirley E. Shepard, BS, MA, JD, LL.M.
        </div>
      </div>
    </section>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function HomePage() {
  return (
    <>
      <NavBar />
      <main className="pt-14" style={{ background: '#0d2218' }}>
        <HeroBanner />
        <CourseOverview />
        <PillarsSection />
        <ObjectivesSection />
        <ModulesSection />
        <FinalProjectSection />
        <InstructorSection />
        <CTABanner />
        <ContactSection />
      </main>
    </>
  );
}
